import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "./icons";

const TIERC: Record<string, string> = { ok: "var(--ok)", warn: "var(--warn)", bad: "var(--bad)" };
const tierOf = (s: number) => (s >= 0.85 ? "ok" : s >= 0.7 ? "warn" : "bad");

export function Frame({ addr, tag, className, children }: { addr?: string; tag?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={"frame" + (className ? " " + className : "")}>
      <div className="frame__bar">
        <div className="frame__dots"><i /><i /><i /></div>
        {addr && <div className="frame__addr"><Icon name="search" size={12} />{addr}</div>}
        {tag && <div className="frame__tag">{tag}</div>}
      </div>
      <div className="frame__body">{children}</div>
    </div>
  );
}

const PROV_SOURCE = [
  { t: "TECHNICAL RIDER — TALE OF US", cls: "hdr" },
  { t: "format: live electronic · 2 members", dim: true },
  { t: "" },
  { t: "DJ BOOTH", label: true },
  { id: "cdj", t: "2× Pioneer CDJ-3000  (linked)" },
  { id: "mix", t: "1× Pioneer DJM-A9 mixer" },
  { t: "MONITORING", label: true },
  { id: "mon", t: "2× Function One F101 wedges" },
  { t: "" },
  { id: "exc", t: "NOTE: absolutely no d&b PA on Main" },
];
const PROV_FIELDS = [
  { id: "cdj", label: "Media player", val: "Pioneer CDJ-3000", note: "×2", score: 0.98 },
  { id: "mix", label: "Mixer", val: "Pioneer DJM-A9", note: "", score: 0.96 },
  { id: "mon", label: "Booth monitors", val: "Function One F101", note: "×2", score: 0.74 },
  { id: "exc", label: "Stage exclusion", val: "No d&b PA · Main", note: "", score: 0.61 },
];

export function ProvenanceMock() {
  const [active, setActive] = useState("mon");
  const wrapRef = useRef<HTMLDivElement>(null);
  const srcRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const fldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [path, setPath] = useState<{ d: string; x1: number; y1: number; x2: number; y2: number } | null>(null);

  const recompute = useCallback(() => {
    const wrap = wrapRef.current, s = srcRefs.current[active], f = fldRefs.current[active];
    if (!wrap || !s || !f) { setPath(null); return; }
    const w = wrap.getBoundingClientRect(), sr = s.getBoundingClientRect(), fr = f.getBoundingClientRect();
    const x1 = sr.right - w.left, y1 = sr.top + sr.height / 2 - w.top, x2 = fr.left - w.left, y2 = fr.top + fr.height / 2 - w.top, mx = (x1 + x2) / 2;
    setPath({ d: `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`, x1, y1, x2, y2 });
  }, [active]);
  useLayoutEffect(() => { recompute(); }, [recompute]);
  useEffect(() => {
    const on = () => recompute();
    window.addEventListener("resize", on);
    const ro = new ResizeObserver(on); if (wrapRef.current) ro.observe(wrapRef.current);
    const t = window.setTimeout(on, 400);
    return () => { window.removeEventListener("resize", on); ro.disconnect(); clearTimeout(t); };
  }, [recompute]);

  return (
    <Frame addr="trace.formray.io/riders/tale-of-us" tag="REVIEW · 4 of 24 need attention">
      <div className="prov" ref={wrapRef}>
        <div className="prov__cols">
          <div className="prov__col prov__col--src">
            <div className="prov__head"><span className="prov__label">Source · rider.pdf</span><span className="prov__label" style={{ color: "var(--text-4)" }}>p.1</span></div>
            <div className="prov__doc">
              {PROV_SOURCE.map((l, i) => {
                if (l.cls === "hdr") return <span key={i} className="prov__line hl" style={{ color: "var(--text)", fontWeight: 700 }}>{l.t}</span>;
                if (l.label) return <span key={i} className="prov__line" style={{ color: "var(--text-4)", letterSpacing: ".1em", fontSize: 10 }}>{l.t}</span>;
                if (!l.t) return <span key={i} className="prov__line" style={{ height: 8 }} />;
                if (l.id) return <span key={i} ref={(el) => { srcRefs.current[l.id!] = el; }} className={"prov__line" + (active === l.id ? " is-linked" : "")} onMouseEnter={() => setActive(l.id!)}>{l.t}</span>;
                return <span key={i} className={"prov__line" + (l.dim ? " prov__line--dim" : "")}>{l.t}</span>;
              })}
            </div>
          </div>
          <div className="prov__col">
            <div className="prov__head"><span className="prov__label">Extracted · reviewed</span><span className="conf-chip" style={{ color: "var(--warn)", background: "color-mix(in oklab, var(--warn) 15%, transparent)" }}><span className="conf-dot" style={{ background: "var(--warn)" }} />0.83 avg</span></div>
            <div className="prov__fields">
              {PROV_FIELDS.map((f) => {
                const tier = tierOf(f.score);
                return (
                  <div key={f.id} ref={(el) => { fldRefs.current[f.id] = el; }} className={"fld" + (active === f.id ? " is-linked" : "")} style={{ ["--tier" as string]: TIERC[tier] }} onMouseEnter={() => setActive(f.id)}>
                    <span className="fld__spine" />
                    <div className="fld__body"><div className="fld__label">{f.label}</div><div className="fld__val">{f.val} {f.note && <em>{f.note}</em>}</div></div>
                    <span className="conf-chip" style={{ color: TIERC[tier], background: `color-mix(in oklab, ${TIERC[tier]} 15%, transparent)` }}><span className="conf-dot" style={{ background: TIERC[tier] }} />{f.score.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <svg className="prov__link-layer">
          {path && <path d={path.d} />}
          {path && <circle className="prov__link-dot" cx={path.x1} cy={path.y1} r={3} />}
          {path && <circle className="prov__link-dot" cx={path.x2} cy={path.y2} r={3} />}
        </svg>
      </div>
    </Frame>
  );
}

const STAGE_C: Record<string, string> = { main: "#22d3ee", stone: "#a78bfa", magma: "#f0a35e" };
export function EquipmentMock() {
  const rows = [["Pioneer CDJ-3000", "8", "4", "2", "14"], ["DJM-A9 mixer", "2", "1", "1", "4"], ["Function One wedge", "6", "2", "4", "12"], ["16A power line", "4", "3", "2", "9"]];
  const stages: [string, string][] = [["main", "Main"], ["stone", "Stone"], ["magma", "Magma"]];
  return (
    <Frame addr="trace · production / equipment" tag="FRI · FESTIVAL-WIDE">
      <div className="eq">
        <div className="eq__head"><span className="eq__title">Equipment totals</span><span className="chip">house + dedicated</span></div>
        <div className="eq__grid">
          <div className="eq__r eq__r--h">
            <span className="eq__c">Item</span>
            {stages.map(([k, n]) => <span key={k} className="eq__c"><span className="stage-dot" style={{ background: STAGE_C[k] }} />{n}</span>)}
            <span className="eq__c">Total</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="eq__r">
              <span className="eq__c">{r[0]}</span><span className="eq__c">{r[1]}</span><span className="eq__c">{r[2]}</span><span className="eq__c">{r[3]}</span><span className="eq__c"><b>{r[4]}</b></span>
            </div>
          ))}
          <div className="eq__r eq__r--total"><span className="eq__c">Rental order</span><span className="eq__c">20</span><span className="eq__c">10</span><span className="eq__c">9</span><span className="eq__c"><b>39</b></span></div>
        </div>
        <div className="eq__foot"><span className="status-badge st-approved"><i />+2 spares added</span><span className="chip">per-day breakdown</span></div>
      </div>
    </Frame>
  );
}
